import type { Limb, Pose } from "./character.js";

export interface Frame {
  pose: Pose;
  ms?: number;
}

export type AnimCategory = "basic" | "talk" | "activity" | "emotion" | "state" | "transition";

export const CATEGORY_NAMES: Record<AnimCategory, string> = {
  basic: "Podstawowe",
  talk: "Rozmowa",
  activity: "Aktywności",
  emotion: "Emocje",
  state: "Stany",
  transition: "Przejścia",
};

export interface Animation {
  id: string;
  label: string;
  category: AnimCategory;
  frameMs: number;
  loop: boolean;
  frames: readonly Frame[];
  /** Where the avatar should stand (room anchor id). */
  anchor?: string;
  /** Walking animations move the avatar (px per second). */
  speed?: number;
  /** Requires an owned item; the gallery shows it anyway. */
  needs?: string;
}

const HANG: Limb = [0, 5, 0, 9];
const UP_L: Limb = [-2, -4, -3, -9];
const UP_R: Limb = [2, -4, 3, -9];
const CHEST_L: Limb = [1, 4, 3, 5];
const CHEST_R: Limb = [-1, 4, -3, 5];

const f = (pose: Pose, ms?: number): Frame => (ms ? { pose, ms } : { pose });

function walkFrames(base: Pose = {}): Frame[] {
  const armL = base.armL;
  const armR = base.armR;
  return [
    f({ ...base, legL: [0, 4, 0, 7], armL: armL ?? [0, 5, -1, 9], armR: armR ?? [0, 5, 0, 8] }),
    f({ ...base, dy: -1, armL: armL ?? HANG, armR: armR ?? HANG }),
    f({ ...base, legR: [0, 4, 0, 7], armL: armL ?? [0, 5, 0, 8], armR: armR ?? [0, 5, 1, 9] }),
    f({ ...base, dy: -1, armL: armL ?? HANG, armR: armR ?? HANG }),
  ];
}

const ANIMS: Animation[] = [
  // Basic
  {
    id: "idle",
    label: "stanie i oddychanie",
    category: "basic",
    frameMs: 600,
    loop: true,
    frames: [
      f({}),
      f({ breath: true }),
      f({}),
      f({ breath: true }),
      f({}),
      f({ eyes: "closed" }, 150),
      f({ breath: true }),
    ],
  },
  {
    id: "walk",
    label: "chodzenie",
    category: "basic",
    frameMs: 150,
    loop: true,
    speed: 22,
    frames: walkFrames({ eyes: "right" }),
  },
  {
    id: "look_around",
    label: "rozglądanie się",
    category: "basic",
    frameMs: 550,
    loop: true,
    frames: [f({ eyes: "left", headDx: -1 }), f({}), f({ eyes: "right", headDx: 1 }), f({ breath: true })],
  },
  {
    id: "scratch_head",
    label: "drapanie po głowie",
    category: "basic",
    frameMs: 220,
    loop: true,
    frames: [
      f({ armR: [4, -3, 1, -9], eyes: "up", overlay: "question" }),
      f({ armR: [4, -3, 0, -10], eyes: "up", overlay: "question" }),
    ],
  },
  {
    id: "sit",
    label: "siadanie na materacu",
    category: "basic",
    frameMs: 700,
    loop: true,
    anchor: "bed",
    frames: [
      f({ orient: "sit", armL: [1, 5, 2, 8], armR: [-1, 5, -2, 8] }),
      f({ orient: "sit", breath: true, armL: [1, 5, 2, 8], armR: [-1, 5, -2, 8] }),
    ],
  },
  // Talk
  {
    id: "talk",
    label: "mówienie",
    category: "talk",
    frameMs: 170,
    loop: true,
    frames: [
      f({ mouth: "open", armR: [3, 3, 4, 6] }),
      f({ mouth: "closed", armR: [3, 3, 4, 6] }),
      f({ mouth: "o", armR: [3, 4, 3, 7] }),
      f({ mouth: "closed", breath: true }),
    ],
  },
  {
    id: "listen",
    label: "słuchanie",
    category: "talk",
    frameMs: 500,
    loop: true,
    frames: [
      f({ armR: [4, -1, 1, -6], overlay: "question", eyes: "right" }),
      f({ armR: [4, -1, 1, -6], breath: true, eyes: "right" }),
    ],
  },
  {
    id: "think",
    label: "myślenie",
    category: "talk",
    frameMs: 380,
    loop: true,
    frames: [
      f({ armR: [2, 3, -3, -3], eyes: "up", overlay: "dots1" }),
      f({ armR: [2, 3, -3, -3], eyes: "up", overlay: "dots2" }),
      f({ armR: [2, 3, -3, -3], eyes: "up", overlay: "dots3" }),
    ],
  },
  {
    id: "nod",
    label: "kiwanie głową",
    category: "talk",
    frameMs: 220,
    loop: true,
    frames: [
      f({ mouth: "smile" }),
      f({ mouth: "smile", headDy: 1, eyes: "down" }),
      f({ mouth: "smile" }),
      f({ mouth: "smile", headDy: 1, eyes: "down" }),
      f({ mouth: "smile" }, 600),
    ],
  },
  // Activities
  {
    id: "pushups",
    label: "trening: pompki",
    category: "activity",
    frameMs: 420,
    loop: true,
    anchor: "training",
    frames: [f({ orient: "prone", raise: 5 }), f({ orient: "prone", raise: 1 })],
  },
  {
    id: "squats",
    label: "trening: przysiady",
    category: "activity",
    frameMs: 420,
    loop: true,
    anchor: "training",
    frames: [
      f({ armL: [1, 1, 3, 2], armR: [-1, 1, -3, 2] }),
      f({
        dy: 4,
        legL: [-2, 3, 0, 4],
        legR: [2, 3, 0, 4],
        armL: [1, 1, 3, 1],
        armR: [-1, 1, -3, 1],
        mouth: "o",
      }),
    ],
  },
  {
    id: "dumbbells",
    label: "trening z hantlami",
    category: "activity",
    frameMs: 420,
    loop: true,
    anchor: "training",
    needs: "dumbbells",
    frames: [
      f({
        props: [
          { id: "dumbbell", at: "handL", dx: -1, dy: -1 },
          { id: "dumbbell", at: "handR", dx: -1, dy: -1 },
        ],
      }),
      f({
        armL: [0, 5, 1, 1],
        armR: [0, 5, -1, 1],
        mouth: "o",
        props: [
          { id: "dumbbell", at: "handL", dx: -1, dy: -1 },
          { id: "dumbbell", at: "handR", dx: -1, dy: -1 },
        ],
      }),
    ],
  },
  {
    id: "pullup",
    label: "podciąganie na drążku",
    category: "activity",
    frameMs: 450,
    loop: true,
    anchor: "door",
    needs: "pullup_bar",
    frames: [
      f({ dy: -6, armL: [-1, -5, -1, -9], armR: [1, -5, 1, -9], legL: [0, 4, 1, 8], legR: [0, 4, -1, 8] }),
      f({
        dy: -10,
        armL: [-3, -2, -1, -5],
        armR: [3, -2, 1, -5],
        mouth: "o",
        legL: [0, 4, 1, 8],
        legR: [0, 4, -1, 8],
      }),
    ],
  },
  {
    id: "read",
    label: "czytanie",
    category: "activity",
    frameMs: 800,
    loop: true,
    frames: [
      f({ armL: CHEST_L, armR: CHEST_R, eyes: "down", props: [{ id: "book", at: "hands", dx: -3, dy: -1 }] }),
      f({
        armL: CHEST_L,
        armR: CHEST_R,
        eyes: "down",
        breath: true,
        props: [{ id: "book", at: "hands", dx: -3, dy: -1 }],
      }),
      f(
        {
          armL: CHEST_L,
          armR: [-1, 3, -2, 3],
          eyes: "down",
          props: [{ id: "book", at: "hands", dx: -3, dy: 1 }],
        },
        300,
      ),
    ],
  },
  {
    id: "laptop",
    label: "praca na laptopie",
    category: "activity",
    frameMs: 200,
    loop: true,
    anchor: "desk",
    frames: [
      f({
        orient: "floor",
        armL: [1, 4, 4, 6],
        armR: [-1, 4, -4, 7],
        eyes: "down",
        props: [{ id: "laptop", at: "ground", dx: -4, dy: -1 }],
      }),
      f({
        orient: "floor",
        armL: [1, 4, 4, 7],
        armR: [-1, 4, -4, 6],
        eyes: "down",
        props: [{ id: "laptop", at: "ground", dx: -4, dy: -1 }],
      }),
    ],
  },
  {
    id: "write",
    label: "pisanie w notesie",
    category: "activity",
    frameMs: 260,
    loop: true,
    frames: [
      f({
        armL: [1, 4, 4, 5],
        armR: [-1, 4, -2, 6],
        eyes: "down",
        props: [{ id: "notebook", at: "handL", dx: -2, dy: -2 }],
      }),
      f({
        armL: [1, 4, 4, 5],
        armR: [-1, 4, -3, 5],
        eyes: "down",
        props: [{ id: "notebook", at: "handL", dx: -2, dy: -2 }],
      }),
    ],
  },
  {
    id: "pick_up",
    label: "podnoszenie",
    category: "activity",
    frameMs: 300,
    loop: true,
    frames: [
      f({
        dy: 2,
        legL: [-1, 3, 0, 6],
        legR: [1, 3, 0, 6],
        armL: [0, 4, 1, 7],
        armR: [0, 4, -1, 7],
        eyes: "down",
      }),
      f({
        dy: 3,
        legL: [-2, 3, 0, 5],
        legR: [2, 3, 0, 5],
        armL: [0, 4, 1, 8],
        armR: [0, 4, -1, 8],
        eyes: "down",
      }),
    ],
  },
  {
    id: "walk_bag",
    label: "wynoszenie śmieci",
    category: "activity",
    frameMs: 160,
    loop: true,
    speed: 18,
    frames: walkFrames({
      eyes: "left",
      armR: [1, 5, 1, 9],
      props: [{ id: "bag_trash", at: "handR", dx: -1, dy: 0 }],
    }),
  },
  {
    id: "sweep",
    label: "sprzątanie miotłą",
    category: "activity",
    frameMs: 300,
    loop: true,
    frames: [
      f({
        armL: [2, 4, 4, 4],
        armR: [0, 4, 2, 7],
        eyes: "down",
        props: [{ id: "broom", at: "handR", dx: 0, dy: -4 }],
      }),
      f({
        armL: [2, 4, 3, 5],
        armR: [0, 4, 4, 7],
        eyes: "down",
        props: [{ id: "broom", at: "handR", dx: 2, dy: -4 }],
      }),
    ],
  },
  {
    id: "vacuum",
    label: "odkurzanie",
    category: "activity",
    frameMs: 300,
    loop: true,
    needs: "vacuum",
    frames: [
      f({ armR: [1, 4, 3, 7], eyes: "down", props: [{ id: "vacuum", at: "handR", dx: 0, dy: 0 }] }),
      f({ armR: [1, 4, 5, 7], eyes: "down", props: [{ id: "vacuum", at: "handR", dx: 2, dy: 0 }] }),
    ],
  },
  {
    id: "cook",
    label: "gotowanie",
    category: "activity",
    frameMs: 300,
    loop: true,
    anchor: "stove",
    frames: [
      f({
        armR: [2, 3, 4, 5],
        eyes: "right",
        props: [{ id: "pan", at: "handR", dx: 0, dy: 0 }],
        overlay: "steam",
      }),
      f({ armR: [2, 2, 4, 3], eyes: "right", props: [{ id: "pan", at: "handR", dx: 0, dy: 0 }] }),
    ],
  },
  {
    id: "eat",
    label: "jedzenie",
    category: "activity",
    frameMs: 380,
    loop: true,
    frames: [
      f({ armL: [1, 4, 3, 6], armR: [-1, 4, -3, 6], props: [{ id: "bowl", at: "handL", dx: -1, dy: -1 }] }),
      f({
        armL: [1, 4, 3, 6],
        armR: [-1, 2, -3, -2],
        mouth: "open",
        props: [{ id: "bowl", at: "handL", dx: -1, dy: -1 }],
      }),
    ],
  },
  {
    id: "walk_shopping",
    label: "zakupy (powrót z siatką)",
    category: "activity",
    frameMs: 160,
    loop: true,
    speed: 18,
    frames: walkFrames({
      eyes: "right",
      armL: [-1, 5, -1, 9],
      props: [{ id: "bag_shop", at: "handL", dx: -2, dy: 0 }],
    }),
  },
  // Emotions
  {
    id: "celebrate",
    label: "świętowanie",
    category: "emotion",
    frameMs: 130,
    loop: true,
    frames: [
      f({ armL: UP_L, armR: UP_R, mouth: "open" }),
      f({ dy: -3, armL: UP_L, armR: UP_R, mouth: "open", legL: [0, 4, -1, 7], legR: [0, 4, 1, 7] }),
      f({ dy: -5, armL: UP_L, armR: UP_R, mouth: "smile", legL: [0, 4, -1, 7], legR: [0, 4, 1, 7] }),
      f({ dy: -3, armL: UP_L, armR: UP_R, mouth: "smile" }),
      f({ armL: [-2, -2, -3, -6], armR: [2, -2, 3, -6], mouth: "smile" }, 260),
    ],
  },
  {
    id: "stage_up",
    label: "awans stadium ciała",
    category: "emotion",
    frameMs: 300,
    loop: true,
    frames: [
      f({ armL: [-4, -1, -3, -5], armR: [4, -1, 3, -5], mouth: "smile", overlay: "spark" }),
      f({ armL: [-4, 0, -3, -5], armR: [4, 0, 3, -5], mouth: "o", breath: true }),
    ],
  },
  {
    id: "carry_box",
    label: "nowy mebel (wnosi przez drzwi)",
    category: "emotion",
    frameMs: 170,
    loop: true,
    speed: 16,
    frames: walkFrames({
      eyes: "right",
      armL: [1, 3, 3, 4],
      armR: [-1, 3, -3, 4],
      props: [{ id: "box", at: "hands", dx: -5, dy: -3 }],
    }),
  },
  // States
  {
    id: "sleep",
    label: "sen",
    category: "state",
    frameMs: 1000,
    loop: true,
    anchor: "bed",
    frames: [f({ orient: "supine", overlay: "zz" }), f({ orient: "supine", breath: true })],
  },
  {
    id: "sick",
    label: "choroba (koc, herbata)",
    category: "state",
    frameMs: 900,
    loop: true,
    anchor: "bed",
    frames: [
      f({
        orient: "sit",
        blanket: true,
        eyes: "down",
        props: [{ id: "mug", at: "lap", dx: -1, dy: -6 }],
        overlay: "steam",
      }),
      f({
        orient: "sit",
        blanket: true,
        eyes: "closed",
        breath: true,
        props: [{ id: "mug", at: "lap", dx: -1, dy: -6 }],
      }),
    ],
  },
  {
    id: "muted",
    label: "wyciszony (słuchawki, czyta)",
    category: "state",
    frameMs: 900,
    loop: true,
    anchor: "bed",
    frames: [
      f({
        orient: "sit",
        headphones: true,
        armL: CHEST_L,
        armR: CHEST_R,
        eyes: "down",
        props: [{ id: "book", at: "hands", dx: -3, dy: -1 }],
        overlay: "note",
      }),
      f({
        orient: "sit",
        headphones: true,
        armL: CHEST_L,
        armR: CHEST_R,
        eyes: "down",
        breath: true,
        props: [{ id: "book", at: "hands", dx: -3, dy: -1 }],
      }),
    ],
  },
  {
    id: "nap",
    label: "drzemka",
    category: "state",
    frameMs: 1000,
    loop: true,
    anchor: "bed",
    frames: [
      f({
        orient: "sit",
        eyes: "closed",
        headDy: 1,
        overlay: "zz",
        armL: [1, 5, 2, 8],
        armR: [-1, 5, -2, 8],
      }),
      f({ orient: "sit", eyes: "closed", headDy: 1, breath: true, armL: [1, 5, 2, 8], armR: [-1, 5, -2, 8] }),
    ],
  },
  {
    id: "idle_state",
    label: "bezczynność",
    category: "state",
    frameMs: 900,
    loop: true,
    frames: [f({ eyes: "down" }), f({ eyes: "down", breath: true }), f({ eyes: "left" })],
  },
  {
    id: "window_look",
    label: "wyglądanie przez okno",
    category: "basic",
    frameMs: 900,
    loop: true,
    anchor: "window",
    frames: [
      f({ back: true }),
      f({ back: true, headDx: -1 }),
      f({ back: true, breath: true }),
      f({ back: true, headDx: 1 }),
    ],
  },
  {
    id: "play_console",
    label: "granie na konsoli",
    category: "activity",
    frameMs: 220,
    loop: true,
    anchor: "console",
    needs: "console",
    frames: [
      f({
        orient: "floor",
        armL: [1, 3, 4, 5],
        armR: [-1, 3, -4, 5],
        eyes: "up",
        props: [{ id: "controller", at: "hands", dx: -2, dy: -1 }],
      }),
      f({
        orient: "floor",
        armL: [1, 3, 4, 4],
        armR: [-1, 3, -4, 5],
        eyes: "up",
        mouth: "o",
        props: [{ id: "controller", at: "hands", dx: -2, dy: -1 }],
      }),
    ],
  },
  {
    id: "guitar",
    label: "granie na gitarze",
    category: "activity",
    frameMs: 260,
    loop: true,
    anchor: "bed",
    needs: "guitar",
    frames: [
      f({
        orient: "sit",
        armL: [-1, 3, 1, 3],
        armR: [1, 4, -2, 6],
        props: [{ id: "guitar", at: "lap", dx: -5, dy: -6 }],
        overlay: "note",
      }),
      f({
        orient: "sit",
        armL: [-1, 3, 1, 3],
        armR: [1, 4, -2, 7],
        props: [{ id: "guitar", at: "lap", dx: -5, dy: -6 }],
        mouth: "smile",
      }),
    ],
  },
  // Transitions
  // Relacje (away from home: café, bar, park) and the office
  {
    id: "coffee_sit",
    label: "kawa przy stoliku",
    category: "activity",
    frameMs: 700,
    loop: true,
    frames: [
      f({
        orient: "sit",
        eyes: "right",
        armL: [1, 4, 3, 5],
        armR: [-1, 5, -2, 8],
        props: [{ id: "mug", at: "handL", dx: 0, dy: -2 }],
      }),
      f(
        {
          orient: "sit",
          eyes: "right",
          mouth: "smile",
          armL: [1, 4, 3, 5],
          armR: [-1, 5, -2, 8],
          props: [{ id: "mug", at: "handL", dx: 0, dy: -2 }],
        },
        1100,
      ),
      f({
        orient: "sit",
        eyes: "right",
        armL: [2, 0, 1, -4],
        armR: [-1, 5, -2, 8],
        props: [{ id: "mug", at: "handL", dx: -1, dy: -2 }],
        overlay: "steam",
      }),
      f(
        {
          orient: "sit",
          eyes: "right",
          mouth: "open",
          armL: [1, 4, 3, 5],
          armR: [-1, 2, -3, 3],
          props: [{ id: "mug", at: "handL", dx: 0, dy: -2 }],
        },
        900,
      ),
    ],
  },
  {
    id: "beer_cheers",
    label: "piwo ze znajomym",
    category: "activity",
    frameMs: 650,
    loop: true,
    frames: [
      f({ eyes: "right", armL: [1, 4, 3, 5], props: [{ id: "beer", at: "handL", dx: 0, dy: -3 }] }, 1200),
      f({
        eyes: "right",
        mouth: "open",
        armL: [1, 4, 3, 5],
        props: [{ id: "beer", at: "handL", dx: 0, dy: -3 }],
      }),
      f({
        eyes: "right",
        mouth: "smile",
        armL: [2, -2, 4, -6],
        props: [{ id: "beer", at: "handL", dx: 0, dy: -3 }],
        overlay: "spark",
      }),
      f({ eyes: "right", armL: [2, 0, 1, -4], props: [{ id: "beer", at: "handL", dx: -1, dy: -3 }] }, 900),
    ],
  },
  {
    id: "stroll",
    label: "wspólny spacer",
    category: "activity",
    frameMs: 220,
    loop: true,
    speed: 12,
    frames: [...walkFrames({ eyes: "right" }), ...walkFrames({ eyes: "right", mouth: "open" })],
  },
  {
    id: "desk_work",
    label: "praca w biurze",
    category: "activity",
    frameMs: 220,
    loop: true,
    frames: [
      f({ orient: "sit", eyes: "right", armL: [1, 4, 5, 3], armR: [-1, 4, 2, 4] }),
      f({ orient: "sit", eyes: "right", armL: [1, 4, 5, 4], armR: [-1, 4, 2, 3] }),
      f({ orient: "sit", eyes: "right", headDx: 1, armL: [1, 4, 5, 3], armR: [-1, 4, 2, 4] }, 1200),
    ],
  },
  {
    id: "exit_door",
    label: "wyjście przez drzwi",
    category: "transition",
    frameMs: 180,
    loop: true,
    speed: 10,
    anchor: "door",
    frames: walkFrames({ back: true }),
  },
  {
    id: "enter_door",
    label: "wejście przez drzwi",
    category: "transition",
    frameMs: 180,
    loop: true,
    speed: 10,
    anchor: "door",
    frames: walkFrames({ eyes: "right" }),
  },
];

export const ANIMATIONS: Readonly<Record<string, Animation>> = Object.fromEntries(
  ANIMS.map((a) => [a.id, a]),
);
export const ANIMATION_LIST: readonly Animation[] = ANIMS;

/** Map idle behaviours from @squire/core to animations. */
export const BEHAVIOR_ANIMATION: Record<string, string> = {
  idle: "idle",
  look_around: "look_around",
  window_look: "window_look",
  scratch_head: "scratch_head",
  walk: "walk",
  sit: "sit",
  nap: "nap",
  read: "read",
  pushups: "pushups",
  squats: "squats",
  pullup: "pullup",
  dumbbells: "dumbbells",
  laptop: "laptop",
  write: "write",
  sweep: "sweep",
  vacuum: "vacuum",
  cook: "cook",
  play_console: "play_console",
  guitar: "guitar",
};

/** Frame at a time offset within an animation. */
export function frameAt(anim: Animation, elapsedMs: number): { index: number; frame: Frame } {
  const total = anim.frames.reduce((s, fr) => s + (fr.ms ?? anim.frameMs), 0);
  let t = anim.loop ? elapsedMs % total : Math.min(elapsedMs, total - 1);
  for (let i = 0; i < anim.frames.length; i++) {
    const fr = anim.frames[i]!;
    t -= fr.ms ?? anim.frameMs;
    if (t < 0) return { index: i, frame: fr };
  }
  const last = anim.frames.length - 1;
  return { index: last, frame: anim.frames[last]! };
}
